<template>
  <Btn
    tag="button"
    data-upgrade-button="1"
    :data-id="dataId"
    :data-upgrade-link="upgradeLink"
    :data-schedule="selectedSchedule"
    :data-currency="selectedCurrency"
    :data-skip-trial="selectedTrialEnabled ? 0 : 1"
    :data-user-quantity="dataUserPlanQuantity"
    :variant="Variant.upgrade"
    icon-position="right"
    icon="arrowRight"
    @click="onClick"
  >
    {{ upgradeText }}
  </Btn>
</template>
<script lang="ts" setup>
import { useStore3 } from '~/store/main';
import type { UpgradeAddon } from '~/lib/services/types';
import Btn from '~/components/form/Btn.vue';
import { Variant } from '~/store/_types';
import type { PlanFeatures, PlanLimits } from '@mailslurp/admin-sdk';
import { storeToRefs } from 'pinia';
import { useRoute, computed, navigateTo } from '#imports';
import { useNuxtApp } from '#imports';
import {
  addonToPlanAddon,
  getPricingPageUrl,
  getUpgradePageAddonUrl,
} from '~/lib/services/upgradeLinks';
const { $captureEvent: captureEvent } = useNuxtApp();
const route = useRoute();
const store3 = useStore3();
const {
  selectedTrialEnabled,
  subscription,
  selectedCurrency,
  selectedSchedule,
  selectedUserPlanQuantity,
} = storeToRefs(store3);

const props = withDefaults(
  defineProps<{
    dataId: string;
    addon?: UpgradeAddon;
    btnText?: string;
    highlightFeature?: keyof PlanFeatures;
    highlightLimit?: keyof PlanLimits;
  }>(),
  {
    btnText: '',
    addon: undefined,
    highlightFeature: undefined,
    highlightLimit: undefined,
  },
);
const upgradeText = computed(() => {
  if (props.btnText) {
    return props.btnText;
  } else if (!selectedTrialEnabled.value) {
    return subscription.value ? 'Change plan' : 'Upgrade';
  } else {
    return 'Start trial';
  }
});
const onClick = async () => {
  captureEvent('click_upgrade_button', {
    dataId: props.dataId,
    route: route?.path,
    addon: props.addon,
    highlightFeature: props.highlightFeature,
    highlightLimit: props.highlightLimit,
    userQuantity: selectedUserPlanQuantity.value,
    schedule: selectedSchedule.value,
    currency: selectedCurrency.value,
    skipTrial: selectedTrialEnabled.value,
  });
  await navigateTo(upgradeLink.value);
};
const dataUserPlanQuantity = computed(() => {
  return selectedUserPlanQuantity.value ?? 0;
});
const upgradeLink = computed(() => {
  if (props.addon) {
    return getUpgradePageAddonUrl(addonToPlanAddon(props.addon), {
      expedite: '1',
    });
  } else if (props.highlightFeature) {
    return getPricingPageUrl({
      highlightFeature: props.highlightFeature,
      highlightLimit: null,
      highlightAddon: null,
      userPlanQuantity: undefined,
      desiresUserPlan: undefined,
      desiresAddonPlan: undefined,
    });
  } else if (props.highlightLimit) {
    return getPricingPageUrl({
      highlightFeature: null,
      highlightLimit: props.highlightLimit,
      highlightAddon: null,
      userPlanQuantity: undefined,
      desiresUserPlan: undefined,
      desiresAddonPlan: undefined,
    });
  } else {
    return '/upgrade/';
  }
});
</script>
